// /var/www/speakiq/frontend/src/pages/superadmin/SuperAdminTenants.js
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import AccountMgmtDropdown from '../../components/AccountMgmtDropdown';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

const PLAN_COLORS = {
  starter: '#64748B', growth: C.teal, pro: '#7C3AED', enterprise: C.warning,
};

function statusBadge(status) {
  const map = {
    active:    { bg: '#D1FAE5', color: C.success,  label: 'Active' },
    suspended: { bg: '#FEF3C7', color: C.warning,  label: 'Suspended' },
    on_hold:   { bg: '#FEF3C7', color: C.warning,  label: 'On Hold' },
    blocked:   { bg: '#FEE2E2', color: C.error,    label: 'Blocked' },
    deleted:   { bg: '#F3F4F6', color: '#7F1D1D',  label: 'Deleted' },
    pending:   { bg: '#EFF6FF', color: '#2563EB',  label: 'Pending' },
  };
  const s = map[status] || map.active;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, letterSpacing: 0.5 }}>
      {s.label}
    </span>
  );
}

function TenantLogo({ tenant }) {
  if (tenant.logo_url) {
    return <img src={tenant.logo_url} alt="" style={{ width: 38, height: 38, borderRadius: 8, objectFit: 'cover', border: '1px solid ' + C.border }} />;
  }
  const initials = (tenant.name || '?').slice(0, 2).toUpperCase();
  const bg = tenant.primary_colour || C.navy;
  return (
    <div style={{ width: 38, height: 38, borderRadius: 8, background: bg, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
      {initials}
    </div>
  );
}

const STATUS_TABS = ['all', 'active', 'pending', 'suspended', 'blocked'];

function LanguageSelector({ tenant, onUpdate }) {
  const [loading, setLoading] = React.useState(false);
  const handleChange = async (e) => {
    const lang = e.target.value;
    setLoading(true);
    try {
      await api.patch(`/api/superadmin/tenants/${tenant.id}/language`, { language: lang });
      onUpdate();
    } catch(err) {
      console.error('Language update failed', err);
    }
    setLoading(false);
  };
  return (
    <select
      value={tenant.language || 'en'}
      onChange={handleChange}
      disabled={loading}
      style={{ padding: '4px 8px', borderRadius: 6, border: '1px solid #E5E7EB', fontSize: 12, cursor: 'pointer', background: '#F9FAFB' }}
    >
      <option value="en">🇬🇧 English</option>
      <option value="ru">🇷🇺 Russian</option>
      <option value="uz">🇺🇿 Uzbek</option>
    </select>
  );
}

const EMPTY_FORM = {
  name: '', subdomain: '', admin_email: '', admin_password: '',
  city: '', plan: 'starter', monthly_fee_usd: '',
};

function toSubdomain(name) {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function CreatePartnerModal({ onClose, onSuccess }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [autoSubdomain, setAutoSubdomain] = useState(true);

  const PLAN_FEES = { starter: 49, growth: 149, pro: 299, enterprise: 499 };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleNameChange = (v) => {
    set('name', v);
    if (autoSubdomain) set('subdomain', toSubdomain(v));
  };

  const handleSubdomainChange = (v) => {
    setAutoSubdomain(false);
    set('subdomain', v.toLowerCase().replace(/[^a-z0-9-]/g, ''));
  };

  const handlePlanChange = (v) => {
    set('plan', v);
    if (!form.monthly_fee_usd || Object.values(PLAN_FEES).includes(Number(form.monthly_fee_usd))) {
      set('monthly_fee_usd', PLAN_FEES[v] || 49);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!form.name.trim()) return setErr('School name is required.');
    if (!form.admin_email.trim()) return setErr('Admin email is required.');
    if (!form.admin_password.trim()) return setErr('Admin password is required.');
    setSaving(true);
    try {
      await api.post('/api/superadmin/tenants', {
        name: form.name.trim(),
        subdomain: form.subdomain.trim() || toSubdomain(form.name),
        admin_email: form.admin_email.trim(),
        admin_password: form.admin_password,
        city: form.city.trim(),
        plan: form.plan,
        monthly_fee_usd: Number(form.monthly_fee_usd) || PLAN_FEES[form.plan],
        tier: 1,
      });
      onSuccess();
    } catch (e) {
      setErr(e.response?.data?.error || 'Failed to create partner. Check subdomain is unique.');
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '9px 12px', border: '1px solid ' + C.border,
    borderRadius: 8, fontSize: 14, outline: 'none', boxSizing: 'border-box',
    color: C.navy,
  };
  const labelStyle = { display: 'block', fontSize: 13, fontWeight: 600, color: C.navy, marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 32, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: C.navy }}>Create New Partner</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.grey, lineHeight: 1 }}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gap: 16 }}>
            <div>
              <label style={labelStyle}>School Name *</label>
              <input style={inputStyle} value={form.name} onChange={e => handleNameChange(e.target.value)} placeholder="e.g. Tashkent Language Academy" />
            </div>

            <div>
              <label style={labelStyle}>Subdomain *</label>
              <div style={{ display: 'flex', alignItems: 'center', border: '1px solid ' + C.border, borderRadius: 8, overflow: 'hidden' }}>
                <input
                  style={{ ...inputStyle, border: 'none', flex: 1, borderRadius: 0 }}
                  value={form.subdomain}
                  onChange={e => handleSubdomainChange(e.target.value)}
                  placeholder="tashkent-academy"
                />
                <span style={{ background: '#F9FAFB', padding: '9px 12px', fontSize: 13, color: C.grey, borderLeft: '1px solid ' + C.border, whiteSpace: 'nowrap' }}>.speakiq.uk</span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Admin Email *</label>
                <input style={inputStyle} type="email" value={form.admin_email} onChange={e => set('admin_email', e.target.value)} placeholder="admin@school.com" />
              </div>
              <div>
                <label style={labelStyle}>Admin Password *</label>
                <input style={inputStyle} type="password" value={form.admin_password} onChange={e => set('admin_password', e.target.value)} placeholder="Min 8 characters" />
              </div>
            </div>

            <div>
              <label style={labelStyle}>City</label>
              <input style={inputStyle} value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Tashkent" />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>Plan</label>
                <select style={{ ...inputStyle, background: '#fff', cursor: 'pointer' }} value={form.plan} onChange={e => handlePlanChange(e.target.value)}>
                  <option value="starter">Starter — $49/mo</option>
                  <option value="growth">Growth — $149/mo</option>
                  <option value="pro">Pro — $299/mo</option>
                  <option value="enterprise">Enterprise — $499/mo</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Monthly Fee (USD)</label>
                <input style={inputStyle} type="number" min="0" value={form.monthly_fee_usd} onChange={e => set('monthly_fee_usd', e.target.value)} placeholder="49" />
              </div>
            </div>
          </div>

          {err && (
            <div style={{ background: '#FEE2E2', color: C.error, padding: '10px 14px', borderRadius: 8, fontSize: 13, marginTop: 16 }}>{err}</div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, padding: '11px', border: '1px solid ' + C.border, borderRadius: 8, background: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', color: C.navy }}>
              Cancel
            </button>
            <button type="submit" disabled={saving} style={{ flex: 2, padding: '11px', background: saving ? '#9CA3AF' : C.teal, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Creating...' : 'Create Partner'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function SuperAdminTenants() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);

  const fetchTenants = () => {
    setLoading(true);
    setError('');
    api.get('/api/superadmin/tenants')
      .then(res => setTenants(res.data.tenants || []))
      .catch(e => setError('Failed to load partners: ' + (e.response?.data?.error || e.message)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchTenants(); }, []);

  const changeAdminEmail = async (tenant) => {
    const cur = tenant.admin_email || '';
    const next = window.prompt(
      `Change the login email for ${tenant.name}.\nThe partner logs in with this address; their password stays the same.`,
      cur);
    if (next === null) return;
    const email = next.trim();
    if (!email || email.toLowerCase() === cur.toLowerCase()) return;
    try {
      await api.patch(`/api/superadmin/tenants/${tenant.id}/admin-email`, { email });
      window.alert(`✅ Login email updated to ${email}. The partner can now log in with it (same password).`);
      fetchTenants();
    } catch (e) {
      window.alert('❌ ' + (e.response?.data?.error || 'Could not update email'));
    }
  };

  const slowPending = useMemo(() =>
    tenants.filter(t => {
      if (t.status !== 'pending') return false;
      if (!t.created_at) return false;
      const hrs = (Date.now() - new Date(t.created_at).getTime()) / 36e5;
      return hrs > 48;
    }),
  [tenants]);

  const filtered = useMemo(() => {
    let list = tenants;
    if (statusFilter !== 'all') list = list.filter(t => (t.status || 'active') === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t =>
        (t.name || '').toLowerCase().includes(q) ||
        (t.subdomain || '').toLowerCase().includes(q) ||
        (t.city || '').toLowerCase().includes(q) ||
        (t.admin_email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [tenants, statusFilter, search]);

  const tabCounts = useMemo(() => {
    const counts = { all: tenants.length };
    STATUS_TABS.slice(1).forEach(t => { counts[t] = tenants.filter(x => (x.status || 'active') === t).length; });
    return counts;
  }, [tenants]);

  const thStyle = { textAlign: 'left', padding: '12px 14px', fontSize: 12, fontWeight: 700, color: C.grey, borderBottom: '2px solid ' + C.border, background: '#F9FAFB', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '12px 14px', fontSize: 14, color: C.navy, borderBottom: '1px solid ' + C.border, verticalAlign: 'middle' };

  return (
    <Layout role="superadmin">
      {showCreate && (
        <CreatePartnerModal
          onClose={() => setShowCreate(false)}
          onSuccess={() => { setShowCreate(false); fetchTenants(); }}
        />
      )}

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '28px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: C.navy, margin: 0 }}>Partner Tenants</h1>
            <p style={{ color: C.grey, fontSize: 14, margin: '2px 0 0' }}>
              {filtered.length} of {tenants.length} partners
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search name, subdomain, city…"
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid ' + C.border, fontSize: 14, width: 240, outline: 'none' }}
            />
            <button
              onClick={() => setShowCreate(true)}
              style={{ padding: '9px 18px', background: C.teal, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
            >
              + Create Partner
            </button>
          </div>
        </div>

        {slowPending.length > 0 && (
          <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 8, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#92400E' }}>
            <span style={{ fontWeight: 700, fontSize: 16 }}>⚠</span>
            <span>
              <strong>{slowPending.length} pending registration{slowPending.length > 1 ? 's' : ''}</strong> awaiting approval for over 48 hours:&nbsp;
              {slowPending.map(t => t.name).join(', ')}
            </span>
          </div>
        )}

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid ' + C.border, flexWrap: 'wrap' }}>
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setStatusFilter(t)}
              style={{
                background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, textTransform: 'capitalize',
                color: statusFilter === t ? C.teal : C.grey,
                borderBottom: statusFilter === t ? '2px solid ' + C.teal : '2px solid transparent',
                marginBottom: -2,
              }}>
              {t} <span style={{ fontSize: 11, background: '#F3F4F6', borderRadius: 10, padding: '1px 7px', marginLeft: 4 }}>{tabCounts[t] || 0}</span>
            </button>
          ))}
        </div>

        {error && <div style={{ background: '#FEE2E2', color: C.error, padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading tenants…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>
            {tenants.length === 0 ? (
              <div>
                <div style={{ fontSize: 48, marginBottom: 12 }}>🏫</div>
                <div style={{ fontSize: 18, fontWeight: 600, color: C.navy, marginBottom: 8 }}>No partners yet</div>
                <div style={{ color: C.grey, marginBottom: 20 }}>Create your first partner to get started.</div>
                <button onClick={() => setShowCreate(true)} style={{ padding: '10px 24px', background: C.teal, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  + Create First Partner
                </button>
              </div>
            ) : 'No tenants match your filters.'}
          </div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid ' + C.border, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Partner</th>
                  <th style={thStyle}>Admin Email</th>
                  <th style={thStyle}>Subdomain</th>
                  <th style={thStyle}>City</th>
                  <th style={thStyle}>Plan</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Students</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tenant => (
                  <tr key={tenant.id} style={{ background: '#fff' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>

                    <td style={tdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <TenantLogo tenant={tenant} />
                        <div>
                          <div style={{ fontWeight: 600 }}>{tenant.name}</div>
                          <div style={{ fontSize: 11, color: C.grey }}>${(tenant.monthly_fee_usd || 0).toFixed(0)}/mo</div>
                        </div>
                      </div>
                    </td>

                    <td style={{ ...tdStyle, fontSize: 13, color: C.grey }}>
                      {tenant.admin_email || '—'}
                    </td>

                    <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: 13, color: C.grey }}>
                      {tenant.subdomain ? `${tenant.subdomain}.speakiq.uk` : '—'}
                    </td>

                    <td style={{ ...tdStyle, color: C.grey }}>{tenant.city || '—'}</td>

                    <td style={tdStyle}>
                      {tenant.plan ? (
                        <span style={{ background: PLAN_COLORS[tenant.plan] || C.grey, color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 4, textTransform: 'capitalize' }}>
                          {tenant.plan}
                        </span>
                      ) : <span style={{ color: C.grey }}>—</span>}
                    </td>

                    <td style={tdStyle}>{statusBadge(tenant.status || 'active')}</td>

                    <td style={{ ...tdStyle, textAlign: 'center', fontWeight: 600 }}>
                      {tenant.student_count ?? 0}
                    </td>

                    <td style={{ ...tdStyle, textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', alignItems: 'center' }}>
                        <a href={`/superadmin/tenants/${tenant.id}`}
                          style={{ fontSize: 12, color: C.teal, fontWeight: 600, textDecoration: 'none', padding: '5px 10px', border: '1px solid ' + C.teal, borderRadius: 6 }}>
                          View
                        </a>
                        <button onClick={() => changeAdminEmail(tenant)} title="Change partner login email"
                          style={{ fontSize: 12, color: C.navy, fontWeight: 600, background: '#fff', padding: '5px 10px', border: '1px solid ' + C.border, borderRadius: 6, cursor: 'pointer' }}>
                          ✉ Email
                        </button>
                        <LanguageSelector tenant={tenant} onUpdate={fetchTenants} />
                        {tenant.status !== 'deleted' && (
                          <AccountMgmtDropdown
                            targetType="partner"
                            targetId={tenant.id}
                            targetName={tenant.name}
                            currentStatus={tenant.status === 'pending' ? 'active' : (tenant.status || 'active')}
                            apiBase="/api/superadmin/partners"
                            onActionComplete={fetchTenants}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
